import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, X, Building2, Check } from 'lucide-react';
import { LoadingSpinner } from './LoadingSpinner';
import { apiClient } from '../../lib/api/client';
import { PortalDropdown } from './PortalDropdown';

interface Client {
  id: string;
  client_name: string;
  status: string;
}

interface ClientFilterDropdownProps {
  value: string;
  onChange: (clientId: string) => void;
  placeholder?: string;
  compact?: boolean;
  className?: string;
}

export const ClientFilterDropdown: React.FC<ClientFilterDropdownProps> = ({
  value,
  onChange,
  placeholder = "Select client...",
  compact = false,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Fetch clients on mount
  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoading(true);
        setError(null);
        const clientList = await apiClient.getActiveClientsList();
        setClients(clientList);
        setFilteredClients(clientList);
      } catch (err: any) {
        console.error('Failed to fetch clients:', err);
        setError('Failed to load clients');
        setClients([]);
        setFilteredClients([]);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, []);

  // Filter clients based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredClients(clients);
    } else {
      const filtered = clients.filter(client =>
        client.client_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredClients(filtered);
    }
  }, [searchQuery, clients]);

  // Close dropdown when clicking outside (handled by PortalDropdown)
  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery('');
  };

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const selectedClient = clients.find(client => client.id === value);

  const handleClientSelect = (clientId: string) => {
    onChange(clientId);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearchQuery('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <div ref={triggerRef} className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        className={`
          w-full flex items-center justify-between px-3 py-2 
          bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 backdrop-blur-xl border border-glass-border/50 dark:border-glass-border-dark-mode/50 
          rounded-lg text-text-enterprise-primary 
          hover:bg-glass-surface/70 dark:hover:bg-glass-surface-dark-mode/70 focus:outline-none focus:ring-2 
          focus:ring-primary-500/50 focus:border-primary-500/50 
          transition-all duration-200
          ${compact ? 'text-sm' : ''}
        `}
        disabled={loading}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {loading ? (
            <LoadingSpinner size={compact ? 'xs' : 'sm'} variant="ai" className="flex-shrink-0 text-primary-400" />
          ) : (
            <Building2 className={`text-primary-400 flex-shrink-0 ${compact ? 'w-3 h-3' : 'w-4 h-4'}`} />
          )}
          <span className={`truncate ${!selectedClient ? 'text-text-enterprise-tertiary' : ''}`}>
            {loading ? 'Loading clients...' : (selectedClient ? selectedClient.client_name : placeholder)}
          </span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {selectedClient && (
            <button
              onClick={handleClear}
              className="p-1 hover:bg-glass-surface/50 dark:hover:bg-glass-surface-dark-mode/50 rounded transition-colors"
              title="Clear selection"
            >
              <X className="w-3 h-3 text-text-enterprise-tertiary" />
            </button>
          )}
          {loading ? (
            <LoadingSpinner size={compact ? 'xs' : 'sm'} variant="ai" className="text-text-enterprise-tertiary" />
          ) : (
            <ChevronDown className={`text-text-enterprise-tertiary transition-transform ${isOpen ? 'rotate-180' : ''} ${compact ? 'w-3 h-3' : 'w-4 h-4'}`} />
          )}
        </div>
      </button>

      {/* Dropdown Menu - Portal */}
      <PortalDropdown
        isOpen={isOpen}
        triggerRef={triggerRef}
        onClose={handleClose}
        className="max-h-80"
      >
        {/* Search Input */}
        <div className="p-3 border-b border-glass-border/50 dark:border-glass-border-dark-mode/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-enterprise-tertiary" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-3 py-2 bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 backdrop-blur-xl border border-glass-border/50 dark:border-glass-border-dark-mode/50 rounded-md text-text-enterprise-primary placeholder-text-enterprise-tertiary focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all duration-200 text-sm"
            />
          </div>
        </div>

        {/* Client List */}
        <div className="max-h-60 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-text-enterprise-tertiary">
              <LoadingSpinner size="sm" variant="ai" className="mx-auto mb-2" />
              Loading clients...
            </div>
          ) : error ? (
            <div className="p-4 text-center text-red-500">
              <div className="text-sm">{error}</div>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="p-4 text-center text-text-enterprise-tertiary">
              {searchQuery ? 'No clients found' : 'No clients available'}
            </div>
          ) : (
            <div className="py-1">
              {filteredClients.map((client) => (
                <button
                  key={client.id}
                  onClick={() => handleClientSelect(client.id)}
                  className={`
                    w-full px-4 py-2 text-left hover:bg-glass-surface/70 dark:hover:bg-glass-surface-dark-mode/70 transition-colors
                    flex items-center justify-between group
                    ${value === client.id ? 'bg-primary-500/10 text-primary-400' : 'text-text-enterprise-primary'}
                  `}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <Building2 className="w-4 h-4 text-primary-400 flex-shrink-0" />
                    <span className="truncate">{client.client_name}</span>
                  </div>
                  {value === client.id && (
                    <Check className="w-4 h-4 text-primary-400 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </PortalDropdown>
    </div>
  );
};

export default ClientFilterDropdown;
